import * as Sentry from '@sentry/browser';
import auth from '~/auth';

import { mapBookingErrorCode } from '~/consts/experiences';

import ApiError from '~/types/ApiError';

const tryParseJSON = (data) => {
  try {
    return JSON.parse(data);
  } catch (e) {
    return data;
  }
};

// Statuses where the response body describes which fields the caller got wrong, rather than a
// server/network fault. These are surfaced to the user verbatim and are not reported to Sentry.
// 422 is what svc-auth returns for "Validation failed" on user updates.
const VALIDATION_ERROR_STATUSES = [400, 422];

export async function request(destination, options?) {
  if (!options) {
    options = {};
  }
  if (options.bustCache) {
    destination = `${destination}?${Math.floor(Date.now() / 1000)}`;
  }

  const token = await auth.getAccessToken();

  if (!token) {
    throw new ApiError({
      message: 'You are not logged in.',
      name: 'AuthorizationError',
    });
  }

  if (!options.headers) {
    options.headers = {};
  }

  if (options.credentials !== 'omit') {
    options.headers['Authorization'] = 'Bearer ' + token;
  }

  options.credentials = options.credentials || 'include';

  return fetch(destination, options).then(function (response) {
    if (options.returnResponse && options.allowNonOkResponse && !response.ok) {
      return response;
    }

    if (response.ok) {
      if (response.status === 204) {
        return true;
      }
      if (options.download) {
        return response.blob();
      }
      if (options.returnResponse) {
        return response;
      }
      if (options.renderHtml) {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('text/html')) {
          return response.text();
        }
      }
      return options.ignoreResponse ? true : response.json();
    } else {
      if (response.status === 403) {
        return response
          .json()
          .catch(function () {
            throw new ApiError({
              message: "You don't have the permissions for this operation",
              name: 'PermissionError',
              status: response.status,
            });
          })
          .then(function (body) {
            throw new ApiError({
              message: body.message || "You don't have the permissions for this operation",
              name: 'PermissionError',
              status: response.status,
              errors: extractValidationErrors(body),
              responseBody: body,
            });
          });
      } else if (response.status === 402) {
        if (options.ignoreResponse) {
          return true;
        }
        return response.json().then(function (body) {
          throw new ApiError({
            message: body.message,
            name: 'Payment failed',
            status: response.status,
            errors: extractValidationErrors(body),
            responseBody: body,
          });
        });
      } else if (VALIDATION_ERROR_STATUSES.includes(response.status) && !options.ignoreResponse) {
        return response.json().then(function (body) {
          const validationErrors = extractValidationErrors(body);
          if (options.errorBodyCallback) {
            options.errorBodyCallback(body, response.status);
          }
          const friendlyMessage = mapBookingErrorCode(body?.message) ?? body?.message;
          if (validationErrors) {
            throw new ApiError({
              message: friendlyMessage || 'Validation error',
              name: 'ValidationError',
              status: response.status,
              errors: validationErrors,
              responseBody: body,
            });
          } else {
            throw new ApiError({
              message: '' + (friendlyMessage || response.status),
              name: 'ValidationError',
              status: response.status,
              responseBody: body,
            });
          }
        });
      } else {
        return response
          .json()
          .then(function (body) {
            if (options.errorBodyCallback) {
              options.errorBodyCallback(body, response.status);
            }
            if (body.message !== 'not found') {
              Sentry.captureException(new Error(body?.message || 'Unknown error'), {
                extra: {
                  body: tryParseJSON(options.body),
                },
              });
            }

            const friendlyMessage = mapBookingErrorCode(body?.message) ?? body?.message ?? 'Unknown error';
            throw new ApiError({
              message: `Network error received (status code ${response.status}): ${friendlyMessage}`,
              status: response.status,
              name: 'NetworkError',
              errors: body.errors,
              responseBody: body,
            });
          })
          .catch(function (err) {
            if (err.name === 'NetworkError') {
              throw err;
            } else {
              const statusMessages: Record<number, string> = {
                502: 'The server is temporarily unavailable. Please try again shortly.',
                503: 'The service is temporarily unavailable. Please try again shortly.',
                504: 'The request timed out. Try a shorter date range or use the email option.',
              };
              const message =
                statusMessages[response.status] ??
                `Unexpected error (status code ${response.status}). Please try again.`;
              throw new ApiError({
                message,
                name: 'NetworkError',
                status: response.status,
              });
            }
          });
      }
    }
  });
}

export const form_headers = {
  'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
};

export const json_headers = {
  'Content-Type': 'application/json;charset=UTF-8',
};

export function objectToFormData(object) {
  const values = [];

  Object.keys(object).forEach(function (key) {
    if (typeof object[key] != 'undefined' && object[key] != null) {
      values.push(encodeURIComponent(key) + '=' + encodeURIComponent(object[key]));
    }
  });

  return values.join('&');
}

function hasErrorShape(obj) {
  return (
    Object.prototype.hasOwnProperty.call(obj, 'status') &&
    obj.status >= 400 &&
    Object.prototype.hasOwnProperty.call(obj, 'message')
  );
}

function extractValidationErrors(responseBody) {
  if (typeof responseBody === 'object' && responseBody.errors) {
    return responseBody.errors;
  }
  if (typeof responseBody === 'object' && Array.isArray(responseBody.issues) && responseBody.issues.length > 0) {
    return responseBody.issues.map((issue) => {
      if (typeof issue === 'string') {
        return issue;
      }

      if (!issue || typeof issue !== 'object') {
        return 'Validation issue';
      }

      const rowNumber = issue.rowNumber || issue.row_number;
      const column = issue.column;
      const reasonCode = issue.reasonCode || issue.reason_code;
      const message = issue.message || 'Validation issue';
      const prefixParts = [];

      if (rowNumber) {
        prefixParts.push(`row ${rowNumber}`);
      }
      if (column) {
        prefixParts.push(`column ${column}`);
      }
      if (reasonCode) {
        prefixParts.push(`${reasonCode}`);
      }

      if (prefixParts.length === 0) {
        return message;
      }

      return `${prefixParts.join(' / ')}: ${message}`;
    });
  }
  if (typeof responseBody === 'object' && responseBody.body?.errors?.length > 0) {
    return responseBody.body.errors;
  }
  if (typeof responseBody === 'object' && responseBody.result?.errors) {
    return responseBody.result.errors;
  }
  // e.g. svc-auth user updates: { status: 422, message: 'Validation failed', result: [{ message: 'Invalid phone number' }] }
  if (typeof responseBody === 'object' && Array.isArray(responseBody.result)) {
    const resultMessages = responseBody.result
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry;
        }
        if (entry && typeof entry === 'object' && typeof entry.message === 'string') {
          return entry.path ? `${entry.path}: ${entry.message}` : entry.message;
        }
        return null;
      })
      .filter(Boolean);

    if (resultMessages.length > 0) {
      return resultMessages;
    }
  }
  if (hasErrorShape(responseBody)) {
    return [responseBody.message];
  }
  return null;
}

export type RequestOptions = {
  signal?: AbortSignal;
};

export type SearchServiceEnvelope<T> = {
  status: number;
  result: T;
};

export const customerPortalHost = (tenant) => window.configs[`${tenant.value.toUpperCase()}_CUSTOMER_PORTAL`];

export const salesforceHost = window.configs.SALESFORCE_HOST;

function isAbortError(error: unknown): boolean {
  return (error as { name?: string })?.name === 'AbortError';
}

export function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
}

export function rethrowAbortError(error: unknown, signal?: AbortSignal) {
  throwIfAborted(signal);

  if (isAbortError(error)) {
    throw error;
  }
}
